<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import AppLayout from '@/layouts/AppLayout.vue'
import BootShell from '@/components/BootShell.vue'
import ToastHost from '@/components/ui/ToastHost.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import GlobalSearch from '@/components/shell/GlobalSearch.vue'
import HelpCenter from '@/components/shell/HelpCenter.vue'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const ui = useUiStore()
const auth = useAuthStore()

const blank = computed(() => route.meta.layout === 'blank' || !auth.isAuthenticated)

function handleResize(): void {
  ui.updateWidth(window.innerWidth)
}

function handleScroll(): void {
  if (route.fullPath) ui.rememberScroll(route.fullPath, window.scrollY)
}

function syncOnline(): void {
  ui.setOnline(navigator.onLine)
}

function restoreViewport(): void {
  const top = ui.takeScroll(route.fullPath)
  requestAnimationFrame(() => {
    if (top > 0) window.scrollTo({ top, behavior: 'auto' })
    if (route.hash) {
      const element = document.getElementById(route.hash.slice(1))
      element?.scrollIntoView({ block: 'start', behavior: 'auto' })
    }
  })
}

watch(() => route.fullPath, async () => {
  await nextTick()
  restoreViewport()
})

onMounted(() => {
  handleResize()
  syncOnline()
  window.addEventListener('resize', handleResize, { passive: true })
  window.addEventListener('scroll', handleScroll, { passive: true })
  window.addEventListener('online', syncOnline)
  window.addEventListener('offline', syncOnline)
  restoreViewport()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('online', syncOnline)
  window.removeEventListener('offline', syncOnline)
})
</script>

<template>
  <BootShell v-if="!ui.booted" />
  <template v-else>
    <template v-if="blank">
      <RouterView />
      <GlobalSearch :open="ui.commandOpen" @close="ui.commandOpen = false" />
      <HelpCenter :open="ui.helpOpen" @close="ui.helpOpen = false" />
    </template>
    <AppLayout v-else>
      <RouterView />
    </AppLayout>
    <ToastHost />
    <ConfirmDialog />
    <div v-if="!ui.online" class="offline-strip" role="status" aria-live="polite">
      <span>网络已断开，页面与筛选条件已保留。</span>
      <RouterLink to="/offline">查看恢复说明</RouterLink>
    </div>
  </template>
</template>
